import React, { useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Handshake, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTNERSHIP_TYPES = [
  "Channel / Reseller",
  "System Integrator",
  "Technology partner",
  "OEM / Distributor",
  "Investment / JV",
  "Other",
];

const empty = {
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  country: "",
  partnership_type: "",
  website: "",
  message: "",
};

export default function BecomePartnerDialog({ open, onOpenChange, triggerTestId = "become-partner-dialog" }) {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.email) {
      toast.error("Please fill company name, your name and a work email.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (form.phone && !/^\+?[\d\s\-()]{6,20}$/.test(form.phone)) {
      toast.error("Phone format looks off — digits, spaces, dashes, parentheses and an optional + only.");
      return;
    }
    if (form.website && !/^https?:\/\/.+/i.test(form.website)) {
      toast.error("Website should start with http:// or https://");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/partners`, form);
      toast.success("Thanks! Our partnerships team will be in touch within 2 business days.");
      setForm(empty);
      onOpenChange(false);
    } catch {
      toast.error("Could not submit — please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid={triggerTestId}>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(27,111,224,0.12)", color: "#1B6FE0" }}>
              <Handshake size={18} weight="duotone" />
            </div>
            <div>
              <DialogTitle className="font-heading text-[20px] tracking-tight" style={{ color: "#0F2A6E", letterSpacing: "-0.01em" }}>
                Become a partner
              </DialogTitle>
              <DialogDescription>
                Tell us about your company. Our partnerships team reviews every request within 2 business days.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={submit} className="mt-2 space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="p-company">Company name *</Label>
              <Input id="p-company" data-testid="partner-company"
                value={form.company_name} onChange={(e) => set("company_name", e.target.value)} required className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="p-name">Your name *</Label>
              <Input id="p-name" data-testid="partner-name"
                value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} required className="mt-1.5" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="p-email">Work email *</Label>
              <Input id="p-email" type="email" data-testid="partner-email"
                value={form.email} onChange={(e) => set("email", e.target.value)} required className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="p-phone">Phone</Label>
              <Input id="p-phone" data-testid="partner-phone"
                placeholder="+971 50 123 4567"
                value={form.phone} onChange={(e) => set("phone", e.target.value.replace(/[^\d+\-\s()]/g, ""))} className="mt-1.5" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="p-country">Country / Region</Label>
              <Input id="p-country" data-testid="partner-country"
                value={form.country} onChange={(e) => set("country", e.target.value)} className="mt-1.5" placeholder="UAE, India, …" />
            </div>
            <div>
              <Label htmlFor="p-website">Company website</Label>
              <Input id="p-website" data-testid="partner-website"
                value={form.website} onChange={(e) => set("website", e.target.value)} className="mt-1.5" placeholder="https://…" />
            </div>
          </div>

          <div>
            <Label>Partnership type</Label>
            <Select value={form.partnership_type} onValueChange={(v) => set("partnership_type", v)}>
              <SelectTrigger data-testid="partner-type" className="mt-1.5">
                <SelectValue placeholder="Select a partnership type" />
              </SelectTrigger>
              <SelectContent>
                {PARTNERSHIP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="p-msg">A few lines about your business and what you'd like to explore</Label>
            <Textarea id="p-msg" data-testid="partner-message"
              rows={4} className="mt-1.5"
              value={form.message} onChange={(e) => set("message", e.target.value)} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="partner-submit"
              className="text-white font-medium" style={{ background: "#1B6FE0" }}>
              {submitting ? "Sending…" : "Submit application"}
              <ArrowRight size={15} weight="bold" />
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
